// dashboard-frontend/src/components/AdminDashboard.jsx
import React, { useEffect, useState } from 'react';
import { fetchJSON } from '../api';
import { Pie } from 'react-chartjs-2';
import 'chart.js/auto';

// Existing component imports
import AdminOnlineDonations from './AdminOnlineDonations';
import AdminUserManagement from './AdminUserManagement';
import AdminCashDonations from './AdminCashDonations';
import AdminSponsorManagement from './AdminSponsorManagement';
import AdminReportRecipients from './AdminReportRecipients';
import AdminInquiries from './AdminInquiries';
import AdminDonorImages from './AdminDonorImages';
import AdminReceiptTemplates from './AdminReceiptTemplates';
import AdminGallery from './AdminGallery';

// Helper functions for date filtering
const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const filterByToday = (donations) => {
  const today = new Date();
  return donations.filter((donation) => isSameDay(new Date(donation.createdAt), today));
};

const getCurrentWeekRange = () => {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
};

const filterByWeek = (donations) => {
  const { monday, sunday } = getCurrentWeekRange();
  return donations.filter((donation) => {
    const dt = new Date(donation.createdAt);
    return dt >= monday && dt <= sunday;
  });
};

const filterByMonth = (donations) => {
  const today = new Date();
  return donations.filter((donation) => {
    const dt = new Date(donation.createdAt);
    return dt.getFullYear() === today.getFullYear() && dt.getMonth() === today.getMonth();
  });
};

const computeMetrics = (onlineArr, cashArr, chequeArr) => {
  const onlineCount = onlineArr.length;
  const onlineAmount = onlineArr.reduce((sum, d) => sum + Number(d.amount), 0);
  const cashCount = cashArr.length;
  const cashAmount = cashArr.reduce((sum, d) => sum + Number(d.amount), 0);
  const chequeCount = chequeArr.length;
  const chequeAmount = chequeArr.reduce((sum, d) => sum + Number(d.amount), 0);
  const totalCount = onlineCount + cashCount + chequeCount;
  const totalAmount = onlineAmount + cashAmount + chequeAmount;
  return {
    onlineCount,
    onlineAmount,
    cashCount,
    cashAmount,
    chequeCount,
    chequeAmount,
    totalCount,
    totalAmount,
  };
};

const getPieData = (metrics) => ({
  labels: ['Online', 'Cash', 'Cheque'],
  datasets: [
    {
      data: [metrics.onlineAmount, metrics.cashAmount, metrics.chequeAmount],
      backgroundColor: ['#4ade80', '#60a5fa', '#facc15'],
    },
  ],
});

const renderDonationSection = (title, metrics) => (
  <div className="mb-12">
    <h2 className="text-2xl font-bold mb-4">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="p-4 bg-white rounded shadow">
        <h3 className="text-xl font-semibold">Online</h3>
        <p>Transactions: {metrics.onlineCount}</p>
        <p>Amount: ₹{metrics.onlineAmount}</p>
      </div>
      <div className="p-4 bg-white rounded shadow">
        <h3 className="text-xl font-semibold">Cash</h3>
        <p>Transactions: {metrics.cashCount}</p>
        <p>Amount: ₹{metrics.cashAmount}</p>
      </div>
      <div className="p-4 bg-white rounded shadow">
        <h3 className="text-xl font-semibold">Cheque</h3>
        <p>Transactions: {metrics.chequeCount}</p>
        <p>Amount: ₹{metrics.chequeAmount}</p>
      </div>
      <div className="p-4 bg-white rounded shadow">
        <h3 className="text-xl font-semibold">Total</h3>
        <p>Transactions: {metrics.totalCount}</p>
        <p>Amount: ₹{metrics.totalAmount}</p>
      </div>
    </div>
    <div className="bg-white p-6 rounded shadow">
      <h3 className="text-xl font-semibold mb-4">Donation Split</h3>
      {metrics.totalAmount > 0 ? (
        <div className="w-full md:w-1/2 mx-auto h-64">
          <Pie
            data={getPieData(metrics)}
            options={{ responsive: true, maintainAspectRatio: false }}
          />
        </div>
      ) : (
        <p className="text-center">No donation data for this period.</p>
      )}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [onlineDonations, setOnlineDonations] = useState([]);
  const [cashDonations, setCashDonations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Retrieve logged-in admin user
  const storedUser = localStorage.getItem('dashboardUser');
  const adminUser = storedUser ? JSON.parse(storedUser) : null;
  const isReadOnly = adminUser && adminUser.role === 'trustee';

  // Decide which tabs are visible to which roles
  let allowedTabs = [];
  if (adminUser) {
    if (adminUser.role === 'graphics') {
      allowedTabs = ['donorImages', 'gallery']; // Graphics can manage donor images and gallery
    } else if (adminUser.role === 'accounts') {
      allowedTabs = ['overview', 'online', 'cash', 'reports'];
    } else if (adminUser.role === 'trustee') {
      allowedTabs = ['overview', 'online', 'cash', 'users', 'sponsors', 'reports'];
    } else {
      // For master_admin or admin – add 'inquiries' and 'donorImages'
      allowedTabs = [
        'overview',
        'online',
        'cash',
        'users',
        'sponsors',
        'receiptTemplates',
        'reports',
        'inquiries',
        'donorImages',
        'gallery',
      ];
    }
  }

  // Fetch data from backend
  const fetchData = async () => {
    try {
      const [onlineData, cashData, userData] = await Promise.all([
        fetchJSON('/api/donations/all'),
        fetchJSON('/api/users/cash-donations'),
        fetchJSON('/api/users'),
      ]);
      setOnlineDonations(onlineData);
      setCashDonations(cashData);
      setUsers(userData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!adminUser) {
      window.location.href = '/admin';
    } else {
      fetchData();
    }
  }, []);

  // Force reload if page is loaded from cache
  useEffect(() => {
    window.onpageshow = function (e) {
      if (e.persisted) {
        window.location.reload();
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('dashboardUser');
    localStorage.removeItem('token');
    window.location.href = '/admin';
  };

  // Filter out only "paid" online donations for metrics
  const overallOnline = onlineDonations.filter((d) => d.status === 'paid');
  const todaysOnline = filterByToday(onlineDonations).filter((d) => d.status === 'paid');
  const weeklyOnline = filterByWeek(onlineDonations).filter((d) => d.status === 'paid');
  const monthlyOnline = filterByMonth(onlineDonations).filter((d) => d.status === 'paid');

  // Separate cashDonations by donationType
  const filterCashCategory = (donations, type) => {
    return donations.filter((d) => {
      if (type === 'cash') {
        return d.donationType === 'cash' || d.donationType === 'upi';
      }
      if (type === 'cheque') {
        return d.donationType === 'cheque';
      }
      return false;
    });
  };

  const overallCashDonations = cashDonations;
  const overallCash = filterCashCategory(overallCashDonations, 'cash');
  const overallCheque = filterCashCategory(overallCashDonations, 'cheque');

  const todaysCashDonations = filterByToday(cashDonations);
  const todaysCash = filterCashCategory(todaysCashDonations, 'cash');
  const todaysCheque = filterCashCategory(todaysCashDonations, 'cheque');

  const weeklyCashDonations = filterByWeek(cashDonations);
  const weeklyCash = filterCashCategory(weeklyCashDonations, 'cash');
  const weeklyCheque = filterCashCategory(weeklyCashDonations, 'cheque');

  const monthlyCashDonations = filterByMonth(cashDonations);
  const monthlyCash = filterCashCategory(monthlyCashDonations, 'cash');
  const monthlyCheque = filterCashCategory(monthlyCashDonations, 'cheque');

  // Compute the metrics
  const overallMetrics = computeMetrics(overallOnline, overallCash, overallCheque);
  const todayMetrics = computeMetrics(todaysOnline, todaysCash, todaysCheque);
  const weekMetrics = computeMetrics(weeklyOnline, weeklyCash, weeklyCheque);
  const monthMetrics = computeMetrics(monthlyOnline, monthlyCash, monthlyCheque);

  // Render donation sections for each period
  const renderOverview = () => (
    <div className="space-y-12">
      {renderDonationSection("Overall Donations", overallMetrics)}
      {renderDonationSection("Today's Donations", todayMetrics)}
      {renderDonationSection("Weekly Donations", weekMetrics)}
      {renderDonationSection("Monthly Donations", monthMetrics)}
    </div>
  );

  // Decide which content to render based on activeTab
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'online':
        return <AdminOnlineDonations readOnly={isReadOnly} />;
      case 'cash':
        return <AdminCashDonations readOnly={isReadOnly} />;
      case 'users':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">User Management</h2>
            <AdminUserManagement readOnly={isReadOnly} />
          </div>
        );
      case 'sponsors':
        return <AdminSponsorManagement readOnly={isReadOnly} />;
      case 'reports':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Email Reports</h2>
            <p className="mb-4">
              Manage report recipients who will receive scheduled (daily/weekly/monthly) emails
              with donation summaries and attached files (CSV, PDF, etc.).
            </p>
            <AdminReportRecipients readOnly={isReadOnly} />
          </div>
        );
      case 'inquiries':
        return <AdminInquiries />;
      case 'donorImages':
        return <AdminDonorImages />;
      case 'gallery':
        return <AdminGallery />;
      case 'receiptTemplates':
        return <AdminReceiptTemplates readOnly={isReadOnly} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar Navigation */}
      <aside className="w-64 bg-dark text-white p-6 hidden md:block">
        <h1 className="text-2xl font-bold mb-8">Dashboard</h1>
        <nav>
          <ul className="space-y-4">
            {allowedTabs.includes('overview') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'overview' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </li>
            )}
            {allowedTabs.includes('online') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'online' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('online')}
              >
                Online Donations
              </li>
            )}
            {allowedTabs.includes('cash') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'cash' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('cash')}
              >
                Cash/Cheque/UPI Donations
              </li>
            )}
            {allowedTabs.includes('users') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'users' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('users')}
              >
                User Management
              </li>
            )}
            {allowedTabs.includes('sponsors') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'sponsors' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('sponsors')}
              >
                Sponsor Management
              </li>
            )}
            {allowedTabs.includes('reports') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'reports' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('reports')}
              >
                Email Reports
              </li>
            )}
            {allowedTabs.includes('inquiries') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'inquiries' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('inquiries')}
              >
                Inquiries
              </li>
            )}
            {allowedTabs.includes('donorImages') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'donorImages' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('donorImages')}
              >
                Daily Donor Images
              </li>
            )}
            {allowedTabs.includes('gallery') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'gallery' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('gallery')}
              >
                Gallery Management
              </li>
            )}
            {allowedTabs.includes('receiptTemplates') && (
              <li
                className={`cursor-pointer hover:text-secondary ${
                  activeTab === 'receiptTemplates' ? 'text-secondary font-bold' : ''
                }`}
                onClick={() => setActiveTab('receiptTemplates')}
              >
                Receipt Templates
              </li>
            )}
          </ul>
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 bg-gray-50 p-6 overflow-y-auto">
        {/* Mobile Navigation Header */}
        <div className="md:hidden flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>

        {/* Desktop Top Header */}
        <div className="hidden md:flex justify-end mb-6">
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>

        {loading ? <p>Loading dashboard data...</p> : renderContent()}
      </main>
    </div>
  );
};

export default AdminDashboard;
